<template>
    <div class="candidate-confirm-step">
        <div class="row">
            <div class="col-md-12">
                <div class="form-group">
                    <label>{{ $t('avatar') }}</label>
                    <div class="custom-image-upload-wrapper">
                        <div class="image-area d-flex">
                            <img 
                                v-if="avatarPreview"
                                :src="avatarPreview"
                                class="img-fluid mx-auto my-auto"
                            >
                        </div> 
                    </div>
                </div>
            </div>
            <div class="col-md-4" v-for="field in primaryFields" :key="field.key">
                <div class="form-group">
                    <label>{{ field.label }}</label>
                    <input type="text"
                        readonly
                    class="form-control"
                    :value="field.value"/>
                </div>
            </div>
            <template v-for="field in customFields">
                <div class="col-md-4" :key="field.name">
                    <div class="form-group">
                        <label>{{ field.label[locate] || field.label['ja'] }}</label>
                        <input type="text" readonly class="form-control" :value="resolveFieldValue(field.name)">
                    </div>
                </div>
            </template>
            <div class="col-md-4" v-for="day in weekDays" :key="`shift-${day.key}`">
                <div class="form-group">
                    <label>{{ day.label }}</label>
                    <input type="text" readonly class="form-control" :value="shiftLabel(day.key)">
                </div>
            </div>
            <div class="col-md-4">
                <div class="form-group">
                    <label>{{ $t('start_date') }}</label>
                    <input type="text" readonly class="form-control" :value="candidateForm.start || placeholder">
                </div>
            </div>
            <div class="col-md-12">
                <div class="form-group">
                    <label>{{ $t('remark') }}</label>
                    <textarea class="form-control" rows="5" readonly>{{ candidateForm.remark || '' }}</textarea>
                </div>
            </div>
        </div>


        <div class="mt-4" v-if="displayTags.length">
            <label class="font-weight-bold d-block">{{ $t('tags') }}</label>
            <div class="d-flex flex-wrap gap-2">
                <span v-for="tag in displayTags" :key="`tag-${tag}`" class="badge badge-pill badge-primary mr-2 mb-2">
                    {{ tag }}
                </span>
            </div>
        </div>

        <div class="mt-4" v-if="resumeFiles.length">
            <label class="font-weight-bold d-block">{{ $t('attachments') }}</label>
            <ul class="list-unstyled mb-0">
                <li v-for="(file, index) in resumeFiles" :key="`file-${index}`">
                    {{ fileLabel(file) }}
                </li>
            </ul>
        </div>

        <div class="d-flex justify-content-between mt-4">
            <button type="button" class="btn btn-dark" @click="goBack">
                {{ $t('back') }}
            </button>
            <button type="submit" class="btn btn-primary">
                {{ $t('submit') }}
            </button>
        </div>
    </div>
</template>

<script>
import FormHelperMixins from "../../../../../../common/Mixin/Global/FormHelperMixins";

export default {
    name: "CandidateConfirmStep",
    mixins: [FormHelperMixins],
    props: {
        props: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        candidateForm() {
            return this.props.formData || {};
        },
        avatarValue() {
            return this.candidateForm.avatar;
        },
        locate() {
            return this.props.locate || 'ja';
        },
        timeMap() {
            const list = this.props.timeList || [];
            return list.reduce((acc, item) => {
                const key = item.id ?? item.value;
                acc[key] = item.name || item.value || item.label || key;
                return acc;
            }, {});
        },
        customFields() {
            return this.props.fields || [];
        },
        resumeFiles() {
            return this.props.files || [];
        },
        placeholder() {
            return '';
        },
        avatarPreview() {
            const avatar = this.avatarValue;
            if (!avatar) {
                return "";
            }

            return URL.createObjectURL(avatar);
        },
        primaryFields() {
            return [
                {key: 'name_romaji', label: this.$t('name_romaji'), value: this.candidateForm.name_romaji || ''},
                {key: 'name_katakana', label: this.$t('name_katakana'), value: this.candidateForm.name_katakana || ''},
                {key: 'email', label: this.$t('email'), value: this.candidateForm.email || ''},
                {key: 'phone', label: this.$t('phone'), value: this.candidateForm.phone || ''},
                {
                    key  : 'gender',
                    label: this.$t('gender'),
                    value: this.candidateForm.gender ? this.$t(this.candidateForm.gender) : ''
                },
                {
                    key  : 'residence',
                    label: this.$t('residence'),
                    value: this.candidateForm.residence_text || this.candidateForm.residence || ''
                },
                {
                    key  : 'clothing_size',
                    label: this.$t('clothing_size'),
                    value: this.candidateForm.clothing_size_text || this.candidateForm.clothing_size || ''
                },
                {
                    key  : 'experience',
                    label: this.$t('experience'),
                    value:
                        this.candidateForm.experience == 1
                            ? this.$t('Yes')
                            : this.candidateForm.experience == 0
                                ? this.$t('No')
                                : ''
                },
                {key: 'in_japan', label: this.$t('years_in_japan'), value: this.candidateForm.in_japan || ''},
                {key: 'job_broker', label: this.$t('job_broker'), value: this.candidateForm.job_broker || ''},
                {key: 'fb_link', label: this.$t('fb_link'), value: this.candidateForm.fb_link || ''},
                {key: 'station', label: this.$t('station'), value: this.candidateForm.station || ''},
                {key: 'how_long', label: this.$t('commuting_time'), value: this.candidateForm.how_long || ''}
            ];
        },
        avatarLabel() {
            const avatar = this.candidateForm.avatar;
            if (!avatar) {
                return "";
            }
            if (typeof avatar === 'string') {
                return avatar.split('/').pop();
            }
            if (avatar.name) {
                return avatar.name;
            }
            return this.$t('added');
        },
        weekDays() {
            return [
                {key: 'monday', label: this.$t('monday')},
                {key: 'tuesday', label: this.$t('tuesday')},
                {key: 'wednesday', label: this.$t('wednesday')},
                {key: 'thursday', label: this.$t('thursday')},
                {key: 'friday', label: this.$t('friday')},
                {key: 'saturday', label: this.$t('saturday')},
                {key: 'sunday', label: this.$t('sunday')}
            ];
        },
        displayTags() {
            const tags = this.candidateForm.tags;
            if (!tags) {
                return [];
            }
            if (Array.isArray(tags)) {
                return tags.map(tag => tag?.title || tag?.name || tag).filter(Boolean);
            }
            return [];
        }
    },
    methods: {
        shiftLabel(dayKey) {
            const value = this.candidateForm[dayKey];
            if (!value) {
                return this.placeholder;
            }
            if (Array.isArray(value)) {
                return value.map(item => this.timeMap[item] || item).join(", ");
            }
            return this.timeMap[value] || value || this.placeholder;
        },
        resolveFieldValue(fieldName) {
            const value = this.candidateForm[fieldName];
            if (value === undefined || value === null || value === "") {
                return this.placeholder;
            }
            if (Array.isArray(value)) {
                return value.map(item => item?.name || item?.label || item).join(", ");
            }
            if (value.name) {
                return value.name;
            }
            if (value.title) {
                return value.title;
            }
            if (typeof File !== "undefined" && value instanceof File) {
                return value.name;
            }
            return value;
        },
        fileLabel(file) {
            if (!file) {
                return "";
            }
            if (typeof file === "string") {
                return file.split("/").pop();
            }
            if (typeof File !== "undefined" && file instanceof File) {
                return file.name;
            }
            return file.name || this.$t('attachment');
        },
        goBack() {
            this.$emit('back', true);
        }
    }
}
</script>
